import { getPrimaryColor, JEJURO } from '@orcar/common-d2c';
import { ReactNode } from 'react';
import styled from 'styled-components';
import HomeFeatureBanner from './HomeFeatureBanner';

const svgProps = {
  width: 26,
  height: 26,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const ClockIcon = () => (
  <svg {...svgProps}>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 7v5l3.5 2' />
  </svg>
);

const PlaneIcon = () => (
  <svg {...svgProps}>
    <path d='M22 2 11 13' />
    <path d='M22 2l-7 20-4-9-9-4 20-7z' />
  </svg>
);

const MoonIcon = () => (
  <svg {...svgProps}>
    <path d='M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z' />
  </svg>
);

type FeatureItem = {
  icon: ReactNode;
  title: string;
  highlight: string;
  description: string;
};

const FEATURES: FeatureItem[] = [
  {
    icon: <ClockIcon />,
    title: '실시간 예약',
    highlight: '24시간',
    description: '보험료 결제까지 한번에. 예약 대기 없이 결제 시 바로 확정.',
  },
  {
    icon: <PlaneIcon />,
    title: '공항에서',
    highlight: '5분 거리',
    description: '공항 셔틀버스 운행으로 안전하고 빠르게 인수·반납.',
  },
  {
    icon: <MoonIcon />,
    title: '새벽반납·야간인수',
    highlight: '가능',
    description: '이른 새벽부터 늦은 밤까지 차량 인수·반납이 가능해요.',
  },
];

type HomeInfoSectionsProps = {
  isMobile: boolean;
};

const HomeInfoSections = ({ isMobile }: HomeInfoSectionsProps) => {
  return (
    <FeatureSection isMobile={isMobile}>
      <Inner isMobile={isMobile}>
        <HomeFeatureBanner isMobile={isMobile} />
        <FeatureGrid isMobile={isMobile}>
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.title}>
              <IconBadge aria-hidden>{feature.icon}</IconBadge>
              <FeatureText>
                <FeatureTitle>
                  {feature.title} <b>{feature.highlight}</b>
                </FeatureTitle>
                <FeatureDesc>{feature.description}</FeatureDesc>
              </FeatureText>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </Inner>
    </FeatureSection>
  );
};

export default HomeInfoSections;

const FeatureSection = styled.section<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: ${({ isMobile }) => (isMobile ? '32px' : '64px')};
  margin-bottom: ${({ isMobile }) => (isMobile ? '48px' : '96px')};
`;

const Inner = styled.div<{ isMobile: boolean }>`
  width: 100%;
  max-width: ${({ theme }) => theme.WIDTH.PC_LAYOUT};
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? '28px' : '44px')};
`;

const FeatureGrid = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) =>
    isMobile ? '1fr' : 'repeat(3, 1fr)'};
  gap: 16px;
`;

const FeatureCard = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 26px 24px;
  border-radius: 18px;
  background: ${({ theme }) => theme.COLOR.WHITE};
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_04};
`;

const IconBadge = styled.span`
  flex: none;
  width: 54px;
  height: 54px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  color: ${getPrimaryColor(JEJURO)};
  background: linear-gradient(
    150deg,
    rgba(0, 145, 232, 0.12),
    rgba(0, 145, 232, 0.04)
  );
`;

const FeatureText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FeatureTitle = styled.h3`
  margin: 0;
  ${({ theme }) => theme.FONT.H4};
  letter-spacing: -0.02em;

  b {
    color: ${getPrimaryColor(JEJURO)};
  }
`;

const FeatureDesc = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B2};
  color: ${({ theme }) => theme.COLOR.GREY_02};
`;
